import React from 'react';
import ShopsHelper from 'helpers/ShopsHelper';
import { numberToWords } from 'helpers/NumberToWordHelper';
import './PrintDocument.css';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';

const PaymentReceipt = ({ data, shop, billFormat, customer }) => {
    const dateValue = data.time || data.created_at;
    const date = dateValue ? new Date(dateValue) : new Date();
    const options = dateValue ? { timeZone: 'UTC' } : {};
    const formattedDate = date.toLocaleDateString('en-GB', options);
    const formattedTime = date.toLocaleTimeString('en-GB', { ...options, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toUpperCase();
    const shopName = billFormat?.shop_name || shop?.shop_name;
    
    let payments = data.sale_payments || [];
    if (payments.length === 0 && data.config?.payment_modes?.length > 0) {
        payments = data.config.payment_modes.map(pm => ({
            mode: pm.name || '-',
            amount: pm.amount
        }));
    } else if (payments.length === 0 && data.credit) {
        payments = [{
            mode: data?.payment_mode?.name || data?.mode?.name || data?.payment_mode_name || data?.payment_mode || data?.mode || '-',
            amount: data.credit
        }];
    }

    const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    
    const customerBalance = Number(customer?.balance || 0);
    const formattedBalance = formatBalanceWithCr(ShopsHelper.getAmountFormatted(shop, customerBalance));

    return (
        <div className="print-document-wrapper print-only">
            {/* Added standard CSS print size rule to match A4Invoice layout */}
            <style type="text/css" media="print">
                {`@page { size: A4 portrait; margin: 10mm 0mm !important; }`}
            </style>

            <div className="print-document-container ize-a4-payment">
                <div className="print-header">
                    {(shop?.shop_logo || billFormat?.shop_logo) && (
                        <div className="logo-container">
                            <img src={shop?.shop_logo || billFormat?.shop_logo} alt="Logo" className="shop-logo" />
                        </div>
                    )}

                    <div className={(shop?.shop_logo || billFormat?.shop_logo) ? "shop-details-right" : "shop-details-center"}>
                        <h1 className="shop-name">{shopName}</h1>
                        <div className="shop-details">
                            {billFormat?.address_1 && (<div>{billFormat?.address_1}</div>)}
                            {billFormat?.address_2 && (<div>{billFormat.address_2}</div>)}
                            {billFormat?.address_3 && (<div>{billFormat.address_3}</div>)}
                            {billFormat?.address_4 && (<div>{billFormat.address_4}</div>)}
                            {(billFormat?.tel_no || shop?.mobile_no || shop?.phone) && (<div>Phone: {billFormat?.tel_no || shop?.mobile_no || shop?.phone}</div>)}
                            {(billFormat?.tin || shop?.trn_number) && (<div>GSTIN: {billFormat?.tin || shop?.trn_number}</div>)}
                        </div>
                    </div>
                </div>
                
                <div className="thick-divider"></div>
                <div className="title-section"><h2 className="document-title">PAYMENT RECEIPT</h2></div>
                <div className="thick-divider"></div>

                {/* Wrapped the entire block inside invoice-body just like A4Invoice to preserve consistent padding and side-borders */}
                <div className="invoice-body">
                    <div className="info-box">
                        <div className="info-col-left">
                            <div className="info-label-cell">Customer :</div>
                            <div className="customer-details">
                                {customer?.name && <div>{customer.name}</div>}
                                {customer?.mobile && <div>{customer.mobile}</div>}
                                {(customer?.location || customer?.address) && (
                                    <div>{customer?.location || customer?.address}</div>
                                )}
                                {customer?.trn_number && (
                                    <div>GSTIN: {customer.trn_number}</div>
                                )}
                            </div>
                        </div>
                        <div className="info-col-right">
                            {data?.id && (
                                <div className="info-row">
                                    <strong>Receipt No :</strong><span>{data.id}</span>
                                </div>
                            )}
                            <div className="info-row"><strong>Date :</strong><span>{formattedDate}</span></div>
                            <div className="info-row"><strong>Time :</strong><span>{formattedTime}</span></div>
                        </div>
                    </div>

                    <table className="items-table">
                        <thead>
                            <tr>
                                <th className="col-index">SN</th>
                                <th>Payment Mode</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((payment, index) => (
                                <tr key={index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>{payment.mode}</td>
                                    <td className="text-right">
                                        {ShopsHelper.getAmountFormatted(shop, payment.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="totals-payment-signature">
                        <table className="summary-table">
                            <tbody>
                                <tr>
                                    <td className="summary-label-cell" style={{ fontWeight: 'bold' }}>Total Paid</td>
                                    <td className="summary-value-cell" style={{ fontWeight: 'bold' }}>{ShopsHelper.getAmountFormatted(shop, totalPaid)}</td>
                                </tr>
                                <tr>
                                    <td className="summary-label-cell" style={{ fontWeight: 'bold' }}> Customer Balance</td>
                                    <td className="summary-value-cell" style={{ fontWeight: 'bold' }}>{formattedBalance}</td>
                                </tr>
                                <tr>
                                    <td colSpan="2" className="summary-text">Amount in words:<strong> {numberToWords(totalPaid)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="footer-signature-box">
                            <div className="bank-details-col">
                                {data.description && (
                                    <div className="receipt-remark"><strong>Description:</strong> {data.description}</div>
                                )}
                                <div className="footer-bottom">
                                    {billFormat?.footer_1 && (<div>{billFormat.footer_1}</div>)}
                                    {billFormat?.footer_2 && (<div>{billFormat.footer_2}</div>)}
                                </div>
                            </div>
                            <div className="signature-col">
                                <div className="signature-label">Authorized Signature</div>
                            </div>
                        </div>
                    </div>
                </div> {/* End of invoice-body */}
            </div> {/* End of print-document-container */}
        </div>
    );
};

export default PaymentReceipt;